import React from 'react';
import { useOutletContext, useLocation, Navigate, Outlet } from 'react-router-dom';
import {
  DEFAULT_REPORT_PATH,
  isReportSuppressedForTenant,
  type TenantSuppressibleReportFlag,
} from '../../config/reportRegistry';
import { getFirstAllowedReportPath } from './reportAccessUtils';

interface AppOutletContext {
  userData?: {
    allowedReports?: string[];
  };
  companyData?: {
    featureFlags?: Record<string, unknown>;
  };
}

interface RequireTenantReportProps {
  /** Suppressible report flag on `companies/{companyId}.featureFlags`. */
  flag: TenantSuppressibleReportFlag;
}

/**
 * Per-tenant report suppression guard (FAIL-OPEN), so a suppressed report is not
 * merely hidden in the nav but unreachable by URL as well.
 *
 * The report is blocked ONLY when the tenant sets `featureFlags.<flag> = false`
 * (an aggregator that can never populate the page, e.g. Finsure supplies no
 * arrears feed). Absent flag, `true`, or any other value falls straight through
 * to the page, so no existing tenant changes. The page and the route are never
 * removed: deleting the flag restores access.
 *
 * Mirrors RequireReportAccess: it redirects rather than rendering an empty page,
 * and never to a path that would bounce straight back here.
 */
const RequireTenantReport: React.FC<RequireTenantReportProps> = ({ flag }) => {
  const outletContext = useOutletContext<AppOutletContext>();
  const location = useLocation();

  if (isReportSuppressedForTenant(outletContext?.companyData?.featureFlags, flag)) {
    // A restricted broker whose only grant is the suppressed report would be
    // redirected back onto it, so fall back to the default report in that case.
    const target = getFirstAllowedReportPath(outletContext?.userData?.allowedReports);
    const safeTarget = target === location.pathname ? DEFAULT_REPORT_PATH : target;
    return <Navigate to={safeTarget} replace />;
  }

  return <Outlet context={outletContext} />;
};

export default RequireTenantReport;
